
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, finalize, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { MasterSonde } from "../domain/Shared/master-sonde";
import { CoreData } from "../tools/core-data";
import { LoadingTask } from "../tools/loading-task";
import { BaseService } from "./base-service";
import { UserService } from "./user-service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Lot } from "../models/lot";

@Injectable({
    providedIn: 'root',
})
export class MasterSondeService extends BaseService {

    private serviceUrl = `${environment.serviceMainBaseUrl}api/masterSonde`;

    constructor(
        protected http: HttpClient,
        protected coreData: CoreData,
        protected loadingTask: LoadingTask,
        protected snackBare: MatSnackBar,
        protected router: Router,
    ) {
        super(coreData, router, snackBare);
    }

    getAllSonde() {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/all`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getAllSonde')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: MasterSonde[]) => {
                return response.map((item: MasterSonde) => {
                    return MasterSonde.mapFromJSON(item);
                });
            })
        );
    }

    getActiveSonde() {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/active`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getActiveSonde')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: MasterSonde[]) => {
                return response.map((item: MasterSonde) => {
                    return MasterSonde.mapFromJSON(item);
                });
            })
        );
    }

    getSondeByReference(ref: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/${ref}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getSondeByReference')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: MasterSonde) => {
                return MasterSonde.mapFromJSON(response);
            })
        );
    }

    getSondeHistoryByReference(ref: string, date?: Date) {
        this.loadingTask.activateLoading();
        let params = new HttpParams();
        if (date) {
            params = params.append('filter', `${date.toISOString()}`);
        }
        return this.http.get(`${this.serviceUrl}/history/${ref}`, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('getSondeHistoryByReference')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: MasterSonde) => {
                return MasterSonde.mapFromJSON(response);
            })
        );
    }

    create(sonde: MasterSonde) {
        this.loadingTask.activateLoading();

        return this.http.post(`${this.serviceUrl}`, sonde, this.generateHeader()).pipe(
            catchError(this.handleError<any>('create')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    update(sonde: MasterSonde) {
        this.loadingTask.activateLoading();

        return this.http.put(`${this.serviceUrl}`, sonde, this.generateHeader()).pipe(
            catchError(this.handleError<any>('update')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    toggleIsActive(sonde: MasterSonde) {
        this.loadingTask.activateLoading();

        return this.http.put(`${this.serviceUrl}/${sonde.Code}/isActive`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('toggleIsActive')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    getLast30Lot() {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/lot/last30`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getLast30Lot')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Lot[]) => {
                return response.map((item: Lot) => {
                    return Lot.mapFromJSON(item);
                });
            })
        );
    }

    getLotByFilter(filter: string) {
        this.loadingTask.activateLoading();
        let params = new HttpParams();
        if (filter) {
            params = params.append('filter', `${filter}`);
        }
        return this.http.get(`${this.serviceUrl}/lot/byFilter/`, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('getLotByFilter')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Lot[]) => {
                return response.map((item: Lot) => {
                    return Lot.mapFromJSON(item);
                });
            })
        );
    }

    deleteLot(numeroLot: string) {
        this.loadingTask.activateLoading();

        return this.http.delete(`${this.serviceUrl}/lot/${numeroLot}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('deleteLot')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }
}